import { useState, useEffect } from 'react';
import {
    X,
    Wallet,
    Plus,
    ArrowUpRight,
    ArrowDownRight,
    ArrowLeft,
    CreditCard,
    QrCode,
    Lock,
    RefreshCw,
    Banknote,
    Coins,
    Clock,
    CheckCircle2,
    AlertCircle,
    Loader2,
    Copy,
    Building2,
    User,
    TrendingUp,
    TrendingDown,
    AlertTriangle,
    Calendar,
    History,
    ChevronDown,
    ChevronUp,
    ShieldCheck,

} from 'lucide-react';
import { api } from "../../config/api";
import {
    createWalletDeposit,
    getWallet,
    pendingPixDeposit,
    getSavedCards,
    depositWithCard,
    withdrawWallet
} from '../../services/walletService';
import PaymentMethodModal from '../PaymentMethodModal/PaymentMethodModal';
import CreditCardForm from '../CreditCardForm/CreditCardForm'; // ajuste o caminho se colocar em outra pasta
import './WalletModal.scss';

export default function WalletModal({ isOpen, onClose, userData, onBalanceUpdate }) {
    const [activeTab, setActiveTab] = useState('balance');
    const [amount, setAmount] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [paymentOpen, setPaymentOpen] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [pixData, setPixData] = useState(null);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [showHistory, setShowHistory] = useState(true);
    const [pendingPix, setPendingPix] = useState(null);
    const [pixKey, setPixKey] = useState('');
    const [pixKeyType, setPixKeyType] = useState('CPF');
    const [walletData, setWalletData] = useState({
        balance: 0,
        cpf: '',
        reserved: 0,
        totalReceived: 0,
        totalWithdrawn: 0,
        transactions: []
    });

    // ---- Estado específico do fluxo de cartão de crédito ----
    // 'amount' -> ainda digitando o valor
    // 'choice' -> tem cartão salvo, perguntando "usar este ou outro"
    // 'form'   -> formulário de cartão novo
    const [cardStep, setCardStep] = useState('amount');
    const [savedCards, setSavedCards] = useState([]);
    const [loadingCards, setLoadingCards] = useState(false);
    const formatCurrency = (value) => {
        return new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
        }).format(Number(value) || 0);
    };

    useEffect(() => {
        if (isOpen) {
            fetchWalletData();
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            const fetchPendingPix = async () => {
                try {
                    const data = await pendingPixDeposit();
                    setPendingPix(data);
                } catch (err) {
                    console.error('Erro ao buscar depósito Pix pendente:', err);
                }
            };
            fetchPendingPix();
        }
    }, [isOpen]);

    // Sempre que trocar o método de pagamento, reseta o sub-fluxo do cartão
    useEffect(() => {
        setCardStep('amount');
        setSavedCards([]);
    }, [paymentMethod]);

    async function fetchWalletData() {
        setIsLoadingData(true);
        setError(null);

        try {
            const data = await getWallet();

            setWalletData({
                balance: data.balance ?? 0,
                reserved: data.reserved ?? 0,
                totalReceived: data.totalReceived ?? 0,
                totalWithdrawn: data.totalWithdrawn ?? 0,
                transactions: data.transactions ?? []
            });

        } catch (err) {
            setError(
                err.response?.data?.message ||
                "Erro ao carregar carteira"
            );
        } finally {
            setIsLoadingData(false);
        }
    }

    async function handleDeposit(paymentMethod) {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await createWalletDeposit({
                amount: parseFloat(amount),
                paymentMethod
            });

            setSuccess('Pagamento criado com sucesso!');

            if (response.qrCodeImage) {
                setPixData({
                    qrCodeImage: response.qrCodeImage,
                    pixPayload: response.pixPayload
                });
            }

            setAmount('');
            await fetchWalletData();
            onBalanceUpdate?.(response.newBalance);
        } catch (err) {
            setError(err.response?.data?.message || 'Erro ao criar pagamento');
        } finally {
            setLoading(false);
        }
    }

    // Chamado quando o valor já foi digitado e o método é CREDIT_CARD.
    // Busca os cartões salvos e decide se mostra a escolha ou já cai no formulário.
    async function handleContinueToCardPayment() {
        setError(null);
        setLoadingCards(true);

        try {
            const cards = await getSavedCards();
            setSavedCards(cards);
            setCardStep(cards.length > 0 ? 'choice' : 'form');
        } catch (err) {
            // Se não conseguir listar os cartões, deixa seguir pro formulário de cartão novo
            setSavedCards([]);
            setCardStep('form');
        } finally {
            setLoadingCards(false);
        }
    }

    // Usuário escolheu usar o cartão já salvo (padrão)
    async function handleUseSavedCard(cardId) {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await depositWithCard({
                amount: parseFloat(amount),
                savedCardId: cardId
            });

            setSuccess('Pagamento realizado com sucesso!');
            setAmount('');
            setCardStep('amount');
            setActiveTab('balance');
            await fetchWalletData();
            onBalanceUpdate?.(response.newBalance);
        } catch (err) {
            setError(err.response?.data?.message || 'Não foi possível concluir o pagamento com este cartão.');
        } finally {
            setLoading(false);
        }
    }

    // Usuário preencheu os dados de um cartão novo
    async function handleSubmitNewCard({ card, saveCard }) {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await depositWithCard({
                amount: parseFloat(amount),
                newCard: card,
                saveCard
            });

            setSuccess('Pagamento realizado com sucesso!');
            setAmount('');
            setCardStep('amount');
            setActiveTab('balance');
            await fetchWalletData();
            onBalanceUpdate?.(response.newBalance);
        } catch (err) {
            setError(err.response?.data?.message || 'Não foi possível processar o pagamento com este cartão.');
        } finally {
            setLoading(false);
        }
    }

    async function handleWithdraw() {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await withdrawWallet({
                amount: parseFloat(amount),
                pixKey,
                pixKeyType
            });

            setSuccess('Saque solicitado! Assim que confirmado pela Asaas, o valor sai da sua carteira.');
            setAmount('');
            setPixKey('');
            await fetchWalletData();
            onBalanceUpdate?.(response.newBalance);

            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Erro ao sacar');
        } finally {
            setLoading(false);
        }
    }

    const availableBalance = walletData.balance - walletData.reserved;

    if (!isOpen) return null;

    // Função para determinar ícone e cor da transação
    const getTransactionConfig = (type, amount) => {
        const configs = {
            income: {
                icon: TrendingUp,
                color: '#059669',
                bgColor: '#ecfdf5',
                label: 'Entrada',
                className: 'positive'
            },
            expense: {
                icon: TrendingDown,
                color: '#dc2626',
                bgColor: '#fef2f2',
                label: 'Saída',
                className: 'negative'
            },
            reserved: {
                icon: Lock,
                color: '#f59e0b',
                bgColor: '#fffbeb',
                label: 'Reservado',
                className: 'neutral'
            },
            refund: {
                icon: RefreshCw,
                color: '#059669',
                bgColor: '#ecfdf5',
                label: 'Reembolso',
                className: 'positive'
            },
            fee: {
                icon: Banknote,
                color: '#6b7280',
                bgColor: '#f3f4f6',
                label: 'Taxa',
                className: 'neutral'
            }
        };
        return configs[type] || configs.income;
    };

    const defaultCard = savedCards.find(c => c.isDefault) ?? savedCards[0];

    return (
        <div className="wm-overlay" onClick={onClose}>
            <div className="wm-modal" onClick={(e) => e.stopPropagation()}>
                <button className="wm-close" onClick={onClose}>
                    <X size={24} />
                </button>

                <div className="wm-header">
                    <div className="wm-header-icon">
                        <Wallet size={24} />
                    </div>
                    <h2 className="wm-title">Minha Carteira</h2>
                </div>

                {isLoadingData ? (
                    <div className="wm-loading">
                        <Loader2 size={32} className="wm-loading-spinner" />
                        <p>Carregando carteira...</p>
                    </div>
                ) : (
                    <>
                        <div className="wm-balance-card">
                            <div className="wm-balance-info">
                                <span className="wm-balance-label">
                                    Saldo disponível
                                </span>

                                <span className="wm-balance-amount">
                                    {formatCurrency(availableBalance)}
                                </span>

                                {walletData.cpf && (
                                    <span className="wm-balance-document">
                                        <User size={14} />
                                        CPF: {walletData.cpf}
                                    </span>
                                )}

                                {walletData.cnpj && (
                                    <span className="wm-balance-document">
                                        <Building2 size={14} />
                                        CNPJ: {walletData.cnpj}
                                    </span>
                                )}
                            </div>

                            {/* <div className="wm-balance-details">
                                <div className="wm-balance-detail">
                                    <TrendingUp size={18} />
                                    <span>Total recebido</span>
                                    <span className="wm-balance-positive">
                                        R$ {walletData.totalReceived.toFixed(2)}
                                    </span>
                                </div>

                                <div className="wm-balance-detail">
                                    <TrendingDown size={18} />
                                    <span>Total sacado</span>
                                    <span className="wm-balance-negative">
                                        R$ {walletData.totalWithdrawn.toFixed(2)}
                                    </span>
                                </div>
                            </div> */}
                        </div>

                        <div className="wm-actions">
                            <button
                                className="wm-action-btn wm-action-btn--deposit"
                                onClick={() => setPaymentOpen(true)}
                            >
                                <span className="wm-action-icon">
                                    <Plus size={24} />
                                </span>
                                <div>
                                    <span className="wm-action-title">Adicionar saldo</span>
                                    <span className="wm-action-sub">Escolha a forma de pagamento</span>
                                </div>
                                <ArrowUpRight size={18} className="wm-action-arrow" />
                            </button>

                            <button
                                className="wm-action-btn wm-action-btn--withdraw"
                                onClick={() => {
                                    setActiveTab('withdraw');
                                    setError(null);
                                    setSuccess(null);
                                }}
                            >
                                <span className="wm-action-icon">
                                    <ArrowDownRight size={24} />
                                </span>
                                <div>
                                    <span className="wm-action-title">Sacar via Pix</span>
                                    <span className="wm-action-sub">Transferir saldo para sua conta</span>
                                </div>
                                <ArrowDownRight size={18} className="wm-action-arrow" />
                            </button>
                        </div>

                        {activeTab === 'withdraw' && (
                            <div className="wm-tab-content">
                                <div className="wm-withdraw">
                                    <div className="wm-withdraw-header">
                                        <div className="wm-withdraw-header-icon">
                                            <ArrowDownRight size={20} />
                                        </div>

                                        <div>
                                            <h4>Solicitar saque</h4>
                                            <p>Transfira seu saldo disponível via Pix.</p>
                                        </div>
                                    </div>

                                    <div className="wm-withdraw-balance">
                                        <div className="wm-withdraw-balance-info">
                                            <span>Saldo disponível</span>
                                            <strong>
                                                R$ {availableBalance.toFixed(2)}
                                            </strong>
                                        </div>

                                        <div className="wm-withdraw-balance-icon">
                                            <Wallet size={20} />
                                        </div>
                                    </div>

                                    <div className="wm-input-group wm-amount-group">
                                        <label htmlFor="withdraw-amount">
                                            Valor do saque
                                        </label>

                                        <div className="wm-money-input">
                                            <span>R$</span>

                                            <input
                                                id="withdraw-amount"
                                                type="number"
                                                step="0.01"
                                                min="1"
                                                max={availableBalance}
                                                placeholder="0,00"
                                                value={amount}
                                                onChange={e => setAmount(e.target.value)}
                                            />
                                        </div>

                                        {parseFloat(amount) > availableBalance ? (
                                            <span className="wm-input-error">
                                                <AlertCircle size={14} />
                                                Saldo insuficiente
                                            </span>
                                        ) : (
                                            <span className="wm-input-hint">
                                                Você pode sacar até R$ {availableBalance.toFixed(2)}
                                            </span>
                                        )}
                                    </div>

                                    <div className="wm-pix-section">

                                        <div className="wm-section-label">
                                            <div>
                                                <span className="wm-section-icon">
                                                    <QrCode size={16} />
                                                </span>

                                                <div>
                                                    <strong>Destino do Pix</strong>
                                                    <small>Informe onde deseja receber</small>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="wm-input-group">
                                            <label htmlFor="pix-key-type">
                                                Tipo da chave Pix
                                            </label>

                                            <div className="wm-select-wrapper">
                                                <select
                                                    id="pix-key-type"
                                                    value={pixKeyType}
                                                    onChange={e => setPixKeyType(e.target.value)}
                                                >
                                                    <option value="CPF">CPF</option>
                                                    <option value="CNPJ">CNPJ</option>
                                                    <option value="EMAIL">E-mail</option>
                                                    <option value="PHONE">Telefone</option>
                                                    <option value="EVP">Chave aleatória</option>
                                                </select>

                                                <ChevronDown size={16} />
                                            </div>
                                        </div>

                                        <div className="wm-input-group">
                                            <label htmlFor="pix-key">
                                                Chave Pix
                                            </label>

                                            <input
                                                id="pix-key"
                                                type="text"
                                                placeholder="Digite sua chave Pix"
                                                value={pixKey}
                                                onChange={e => setPixKey(e.target.value)}
                                            />
                                        </div>

                                    </div>

                                    <div className="wm-withdraw-security">
                                        <ShieldCheck size={16} />

                                        <span>
                                            Seu saque será processado com segurança e enviado para a chave Pix informada.
                                        </span>
                                    </div>

                                    <button
                                        className="wm-submit-btn"
                                        onClick={handleWithdraw}
                                        disabled={
                                            loading ||
                                            !amount ||
                                            parseFloat(amount) <= 0 ||
                                            parseFloat(amount) > availableBalance
                                        }
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 size={18} className="wm-spinner" />
                                                Processando saque...
                                            </>
                                        ) : (
                                            <>
                                                <ArrowDownRight size={18} />
                                                Solicitar saque
                                            </>
                                        )}
                                    </button>

                                </div>
                            </div>
                        )}

                        {activeTab === 'deposit' && (
                            <div className="wm-tab-content">
                                <div className="wm-tab-header">
                                    <button
                                        className="wm-tab-btn wm-tab-btn--active"
                                        onClick={() => {
                                            setError(null);
                                            setSuccess(null);
                                            setAmount('');
                                            setCardStep('amount');
                                        }}
                                    >
                                        <Plus size={16} />
                                        Adicionar saldo
                                    </button>
                                </div>

                                <div className="wm-deposit">
                                    <h4>
                                        <CreditCard size={18} />
                                        Depósito via {
                                            paymentMethod === 'PIX'
                                                ? 'Pix'
                                                : paymentMethod === 'CREDIT_CARD'
                                                    ? 'Cartão de crédito'
                                                    : 'Boleto'
                                        }
                                    </h4>

                                    {/* ---- PIX: fluxo original, sem alteração ---- */}
                                    {paymentMethod === 'PIX' && (
                                        <>
                                            <div className="wm-input-group">
                                                <label>Valor do depósito</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="5"
                                                    placeholder="R$ 0,00"
                                                    value={amount}
                                                    onChange={e => setAmount(e.target.value)}
                                                />
                                                <span className="wm-input-hint">Valor mínimo: R$ 5,00</span>

                                                {amount && parseFloat(amount) < 5 && (
                                                    <span className="wm-input-error">
                                                        <AlertCircle size={14} />
                                                        Valor mínimo de recarga é R$5,00
                                                    </span>
                                                )}
                                            </div>

                                            <button
                                                className="wm-submit-btn"
                                                onClick={() => handleDeposit('PIX')}
                                                disabled={loading || !amount || parseFloat(amount) < 5}
                                            >
                                                {loading ? (
                                                    <>
                                                        <Loader2 size={18} className="wm-spinner" />
                                                        Processando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Plus size={18} />
                                                        Adicionar saldo
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    )}

                                    {/* ---- CARTÃO DE CRÉDITO ---- */}
                                    {paymentMethod === 'CREDIT_CARD' && (
                                        <>
                                            {cardStep === 'amount' && (
                                                <>
                                                    <div className="wm-input-group">
                                                        <label>Valor do depósito</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="5"
                                                            placeholder="R$ 0,00"
                                                            value={amount}
                                                            onChange={e => setAmount(e.target.value)}
                                                        />
                                                        <span className="wm-input-hint">Valor mínimo: R$ 5,00</span>

                                                        {amount && parseFloat(amount) < 5 && (
                                                            <span className="wm-input-error">
                                                                <AlertCircle size={14} />
                                                                Valor mínimo de recarga é R$5,00
                                                            </span>
                                                        )}
                                                    </div>

                                                    <button
                                                        className="wm-submit-btn"
                                                        onClick={handleContinueToCardPayment}
                                                        disabled={loadingCards || !amount || parseFloat(amount) < 5}
                                                    >
                                                        {loadingCards ? (
                                                            <>
                                                                <Loader2 size={18} className="wm-spinner" />
                                                                Verificando cartões...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <CreditCard size={18} />
                                                                Continuar
                                                            </>
                                                        )}
                                                    </button>
                                                </>
                                            )}

                                            {cardStep === 'choice' && defaultCard && (
                                                <div className="wm-card-choice">
                                                    <div className="wm-saved-card">
                                                        <CreditCard size={20} />
                                                        <div>
                                                            <strong>{defaultCard.brand} •••• {defaultCard.lastFourDigits}</strong>
                                                            <small>{defaultCard.holderName}</small>
                                                        </div>
                                                    </div>

                                                    <button
                                                        className="wm-submit-btn"
                                                        onClick={() => handleUseSavedCard(defaultCard.id)}
                                                        disabled={loading}
                                                    >
                                                        {loading ? (
                                                            <>
                                                                <Loader2 size={18} className="wm-spinner" />
                                                                Processando...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <CheckCircle2 size={18} />
                                                                Usar este cartão — R$ {parseFloat(amount).toFixed(2)}
                                                            </>
                                                        )}
                                                    </button>

                                                    <button
                                                        className="wm-secondary-btn"
                                                        onClick={() => setCardStep('form')}
                                                        disabled={loading}
                                                    >
                                                        Usar outro cartão
                                                    </button>

                                                    <button
                                                        className="wm-link-btn"
                                                        onClick={() => setCardStep('amount')}
                                                        disabled={loading}
                                                    >
                                                        <ArrowLeft size={14} />
                                                        Alterar valor
                                                    </button>
                                                </div>
                                            )}

                                            {cardStep === 'form' && (
                                                <CreditCardForm
                                                    onSubmit={handleSubmitNewCard}
                                                    onCancel={savedCards.length > 0 ? () => setCardStep('choice') : () => setCardStep('amount')}
                                                    submitting={loading}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {pendingPix && !pixData && (
                            <div className="wm-pix-banner" onClick={() => setPixData(pendingPix)}>
                                <QrCode size={18} />
                                <span>Você tem um código Pix pendente. Toque para pagar.</span>
                            </div>
                        )}

                        {success && (
                            <div className="wm-success">
                                <CheckCircle2 size={18} />
                                {success}
                            </div>
                        )}
                        {error && (
                            <div className="wm-error">
                                <AlertCircle size={18} />
                                {error}
                            </div>
                        )}

                        <div className="wm-info">
                            <h4 className="wm-info-title">
                                <AlertCircle size={18} />
                                Como funciona a carteira?
                            </h4>
                            <ul className="wm-info-list">
                                <li><strong>Empresa:</strong> paga a vaga → valor fica retido</li>
                                <li>Ao finalizar o serviço, o valor vai para o freelancer</li>
                                <li>O freelancer pode sacar via Pix</li>
                            </ul>
                        </div>

                        {walletData.transactions && walletData.transactions.length > 0 && (
                            <div className="wm-transactions">
                                <div
                                    className="wm-transactions-header"
                                    onClick={() => setShowHistory(!showHistory)}
                                >
                                    <div className="wm-transactions-title-group">
                                        <History size={18} />
                                        <h4 className="wm-transactions-title">Últimas transações</h4>
                                    </div>
                                    <button className="wm-transactions-toggle">
                                        {showHistory ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                    </button>
                                </div>

                                {showHistory && (
                                    <div className="wm-transactions-list">
                                        {walletData.transactions.map(transaction => {
                                            const config = getTransactionConfig(transaction.type, transaction.amount);
                                            const Icon = config.icon;

                                            return (
                                                <div key={transaction.id} className="wm-transaction">
                                                    <div className="wm-transaction-info">
                                                        <div
                                                            className="wm-transaction-icon-wrapper"
                                                            style={{ backgroundColor: config.bgColor }}
                                                        >
                                                            <Icon size={18} style={{ color: config.color }} />
                                                        </div>
                                                        <div>
                                                            <p className="wm-transaction-description">
                                                                {transaction.description}
                                                                <span className="wm-transaction-type">
                                                                    {config.label}
                                                                </span>
                                                            </p>
                                                            <span className="wm-transaction-date">
                                                                <Calendar size={12} />
                                                                {new Date(transaction.date).toLocaleDateString('pt-BR')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="wm-transaction-amount">
                                                        <span
                                                            className={`wm-transaction-value wm-transaction-value--${config.className}`}
                                                        >
                                                            {transaction.amount > 0 ? '+' : ''}
                                                            R$ {Math.abs(transaction.amount).toFixed(2)}
                                                        </span>
                                                        <span
                                                            className={`wm-transaction-status wm-transaction-status--${transaction.status}`}
                                                        >
                                                            {transaction.status === 'completed' ? (
                                                                <CheckCircle2 size={14} />
                                                            ) : transaction.status === 'failed' ? (
                                                                <AlertCircle size={14} />
                                                            ) : (
                                                                <Clock size={14} />
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        <PaymentMethodModal
                            isOpen={paymentOpen}
                            onClose={() => setPaymentOpen(false)}
                            onSelect={(method) => {
                                setPaymentMethod(method);
                                setPaymentOpen(false);
                                setActiveTab('deposit');
                                setCardStep('amount');
                            }}
                        />

                        {pixData && (
                            <div className="wm-pix-overlay" onClick={() => setPixData(null)}>
                                <div className="wm-pix-modal" onClick={(e) => e.stopPropagation()}>
                                    <button className="wm-pix-close" onClick={() => setPixData(null)}>
                                        <X size={24} />
                                    </button>
                                    <div className="wm-pix-icon">
                                        <QrCode size={48} />
                                    </div>
                                    <h3>Pague com Pix</h3>
                                    <img
                                        src={`data:image/png;base64,${pixData.qrCodeImage}`}
                                        alt="QR Code Pix"
                                        className="wm-pix-qrcode"
                                    />
                                    <div className="wm-pix-copy">
                                        <input type="text" readOnly value={pixData.pixPayload} />
                                        <button onClick={() => navigator.clipboard.writeText(pixData.pixPayload)}>
                                            <Copy size={16} />
                                            Copiar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}